import {defineStore, acceptHMRUpdate, storeToRefs} from 'pinia'
import {ref, computed, nextTick, triggerRef} from 'vue';
import axios from "axios";
import {useRestapi} from "./restapi";
import {attachUninterceptedErrorReporting} from "../services/apiErrorReporting";
import {useSessionStore} from "./session";
import {useRegionStore} from "./region";
import {toStoreError} from "../services/apiClientError";
import {intersection} from "../util";
import {Loading, Notify, useQuasar} from 'quasar'
import { date } from 'quasar'


export const useUserStore = defineStore('user', () => {
  const sessionStore = useSessionStore();
  
  // Expose session data as computed properties for reactivity
  const user = computed(() => sessionStore.user);
  const loggedIn = computed(() => sessionStore.loggedIn);
  const lti_launch = computed(() => sessionStore.lti_launch);
  const device = computed({
    get: () => sessionStore.device,
    set: (value) => sessionStore.device = value
  });
  const deviceLoaded = computed(() => sessionStore.deviceLoaded);
  const setDevice = (value) => sessionStore.setDevice(value);
  const ensureDevice = () => sessionStore.ensureDevice();
  const passkey_abort_controller = ref(null);
  
  // Password compromise status - exposed from session store
  const passwordCompromised = computed(() => sessionStore.passwordCompromised);
  const showPasswordCompromiseBanner = computed(() => sessionStore.showPasswordCompromiseBanner);

  const restapi = useRestapi();

  const $q = useQuasar()

  axios.defaults.withCredentials = true

  const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    headers: {
      'Content-Type': 'application/json'
    }
  });
  api.interceptors.response.use(restapi.intercept, restapi.error_intercept);

  // The whole authentication surface (/login, /logout, /login_check,
  // /verify_email, /become_user, /create_otp, /verify_otp).
  // It opts out of restapi.js's error_intercept because these callers render
  // their own 4xx messages and must not be toasted or redirected — but it still
  // gets reporting-only triage so a real outage here is visible in Datadog.
  const api_no_intercept = attachUninterceptedErrorReporting(axios.create({
    baseURL: import.meta.env.VITE_API_URL,
    headers: {
      'Content-Type': 'application/json'
    }
  }));

  const org_for_user = computed(() => {
    if (!user.value) return null;
    if (!sessionStore.organization) return null;
    for (let o of user.value.schools) {
      if (o.organization_uid === sessionStore.organization.uid) return o;
    }
  });

  const default_route = computed(() => {
    if (!user.value) return '/';
    if (intersection(['Superuser', 'Support'], user.value.roles).length) return '/user';
    if (intersection(['Content Manager'], user.value.roles).length) return '/template_patients';
    let org = org_for_user.value;
    if (org) {
      if (intersection(['Instructor', 'Organization Admin', 'Organization Content Admin', 'Program Admin', 'Campus Admin', 'Program Content Admin', 'Campus Content Admin', 'Adjunct'], org.roles).length) return '/dashboard';
    } else if (intersection(['Demo'], user.value.roles).length) return '/demo';
    return '/student'
  });

  const user_role = computed(() => {
    if (!user.value) return 'Unauthenticated';
    if (intersection(['Superuser'], user.value.roles).length) return 'Superuser';
    if (intersection(['Support'], user.value.roles).length) return 'Support';
    if (intersection(['Content Manager'], user.value.roles).length) return 'Content Manager';
    let org = org_for_user.value;
    if (org) {
      if (intersection(['Instructor'], org.roles).length) return 'Instructor';
      if (intersection(['Organization Admin'], org.roles).length) return 'Organization Admin';
      if (intersection(['Organization Content Admin'], org.roles).length) return 'Organization Content Admin';
      if (intersection(['Program Admin'], org.roles).length) return 'Program Admin';
      if (intersection(['Campus Admin'], org.roles).length) return 'Campus Admin';
      if (intersection(['Program Content Admin'], org.roles).length) return 'Program Content Admin';
      if (intersection(['Campus Content Admin'], org.roles).length) return 'Campus Content Admin';
      if (intersection(['Adjunct'], org.roles).length) return 'Adjunct';      
    };
    return 'Student'
  });

  // ChartFlow staff. Creating an LTI registration or deployment, and changing a
  // registration's issuer, client ID, key set, login and token URLs or a
  // deployment's registration and deployment ID, is staff-only on the backend;
  // the Integrations screens use this to avoid offering what would be refused.
  const is_staff = computed(() => {
    if (!user.value) return false;
    return intersection(['Superuser', 'Support'], user.value.roles || []).length > 0;
  });

  const manage_content = computed(() => {
    if (!user.value) return false;
    return intersection(['Superuser', 'Content Manager'], user.value.roles).length > 0;
  });

  // Effective value of a display preference. The backend sends every preference
  // with its default already applied, so a missing map here means the user has
  // not loaded yet rather than "off".
  const preference = (key) => user.value?.preferences?.[key] ?? null;

  // The Integrations tab is LTI and SAML configuration. Superuser and Support
  // always see it; for Organization Admins it is opt-in, because most of them
  // never touch it. This is a display choice, not a permission — an Org Admin
  // who turns it on is entitled to everything in it either way.
  const show_integrations_tab = (organization_uid) => {
    if (check_roles(['Superuser', 'Support'], organization_uid)) return true;
    if (!check_roles(['Organization Admin'], organization_uid)) return false;
    return preference('show_integrations') === true;
  };

  // Does the user hold one of these roles in ANY school?
  //
  // Org-level roles live on `school.roles`, so `check_roles` cannot answer
  // without an organization_uid — omitting it silently returns false (see
  // user.checkRolesOrgScoped.test.js). The Profile page has no single
  // organization in context, so it needs this form instead.
  const has_role_in_any_school = (roles) => {
    if (!user.value) return false;
    if (intersection(roles, user.value.roles || []).length > 0) return true;
    return (user.value.schools || []).some(
      (school) => intersection(roles, school.roles || []).length > 0
    );
  };

  const check_roles = (roles, organization_uid, program_uid, campus_uid, course_uid) => {
    let has_roles = [];
    if (!user.value) return false;
    if (user.value.roles && user.value.roles.length > 0) {
      has_roles.push(...user.value.roles);
    }
    const school = user.value.schools.find(school => school.organization_uid === organization_uid);
    let program = null;
    let course = null;
    let campus = null;
    if (school) {
      program = school.programs.find(program => program.program_uid === program_uid);
      campus = school.campuses.find(campus => campus.campus_uid === campus_uid);
      course = school.courses.find(course => course.uid === course_uid);
      has_roles.push(...intersection(school.roles, ['Organization Admin', 'Organization Content Admin']));
      if(intersection(roles, ['Instructor', 'Adjunct']).length > 0){
        has_roles.push(...intersection(school.roles, ['Instructor', 'Adjunct']));
      }
    }
    if (program) {
      has_roles.push(...intersection(program.roles, ['Program Admin', 'Program Content Admin']));
      if(intersection(roles, ['Instructor', 'Adjunct']).length > 0){
        has_roles.push(...intersection(program.roles, ['Instructor', 'Adjunct']));
      }
    }
    if (campus) {
      has_roles.push(...intersection(campus.roles, ['Campus Admin', 'Campus Content Admin']));
      if(intersection(roles, ['Instructor', 'Adjunct']).length > 0){
        has_roles.push(...intersection(campus.roles, ['Instructor', 'Adjunct']));
      }
    }
    if (course) {
      has_roles.push(...course.roles);
    }
    return intersection(roles, has_roles).length > 0;
  };

  // check_roles only inspects course-level roles when a specific course_uid
  // is passed in — Adjuncts are typically assigned Instructor-equivalent
  // access on a single course rather than org-wide, so nav-level checks
  // (which have no single course in context) need this instead.
  const has_role_in_any_course = (roles, organization_uid) => {
    if (!user.value) return false;
    const school = user.value.schools.find(school => school.organization_uid === organization_uid);
    if (!school || !school.courses) return false;
    return school.courses.some(course => intersection(roles, course.roles || []).length > 0);
  };

  const check_license = () => {
    let current_date = date.addToDate(new Date(), {minutes: 5})
    for (let license of user.value.licenses) {
      if (new Date(license.start_date) <= current_date && new Date(license.end_date) >= new Date()) return true;
    }
    return false;
  };

  const programs_for_organization = (organization_uid) => {
    const school = user.value.schools.find(school => school.organization_uid === organization_uid);
    if (!school) return [];
    return school.programs;
  };

  const campuses_for_organization = (organization_uid) => {
    const school = user.value.schools.find(school => school.organization_uid === organization_uid);
    if (!school) return [];
    return school.campuses;
  };

  const get_user_program = (organization_uid, program_uid) => {
    const school = user.value.schools.find(school => school.organization_uid === organization_uid);
    if (!school) return null;
    return school.programs.find(program => program.program_uid === program_uid);
  };

  const get_user_campus = (organization_uid, campus_uid) => {
    const school = user.value.schools.find(school => school.organization_uid === organization_uid);
    if (!school) return null;
    return school.campuses.find(campus => campus.campus_uid === campus_uid);
  };

  const available_roles = (organization_uid, program_uid, campus_uid, course_uid, cohort_uid) => {
    if (!user.value) return null;
    const school = user.value.schools.find(school => school.organization_uid === organization_uid);
    let program = null;
    let course = null;
    let campus = null;
    if (school) {
      program = school.programs.find(program => program.program_uid === program_uid);
      campus = school.campuses.find(campus => campus.campus_uid === campus_uid);
      course = school.courses.find(course => course.uid === course_uid);
    }
    let potential_roles = [];
    if (cohort_uid) {
      return ['Student'];
    }
    else if (course_uid) {
      potential_roles = ['Student', 'Instructor', 'Adjunct']
      if (intersection(user.value.roles, ['Superuser', 'Support'])) return potential_roles;
      if (school && 'Organization Admin' in school.roles) return potential_roles;
      if (program && 'Program Admin' in program.roles) return potential_roles;
      if (campus && 'Campus Admin' in campus.roles) return potential_roles;
      if (course && 'Instructor' in course.roles) return potential_roles;
    } else if (program_uid) {
      potential_roles = ['Student', 'Instructor', 'Adjunct', 'Program Admin', 'Program Content Admin']
      if (intersection(user.value.roles, ['Superuser', 'Support'])) return potential_roles;
      if (school && 'Organization Admin' in school.roles) return potential_roles;
      if (program && 'Program Admin' in program.roles) return potential_roles;
      if (program && 'Program Content Admin' in program.roles) return ['Program Content Admin'];
    } else if (campus_uid) {
      // Campus Content Admin mirrors Program Content Admin one level up. It is
      // granted by the same people who may grant Campus Admin — the backend's
      // CAMPUS_MEMBERSHIP_ROLES (role_grant_authorization.py) — and a Campus
      // Content Admin cannot grant anything, so it gets no branch of its own.
      potential_roles = ['Student', 'Instructor', 'Adjunct', 'Campus Admin', 'Campus Content Admin']
      if (intersection(user.value.roles, ['Superuser', 'Support'])) return potential_roles;
      if (school && 'Organization Admin' in school.roles) return potential_roles;
      if (campus && 'Campus Admin' in campus.roles) return potential_roles;
    } else if (organization_uid) {
      potential_roles = ['Student', 'Instructor', 'Adjunct', 'Organization Admin', 'Organization Content Admin', 'Billing']
      if (intersection(user.value.roles, ['Superuser', 'Support'])) return potential_roles;
      if (school && 'Organization Admin' in school.roles) return potential_roles;
      if (school && 'Organization Content Admin' in school.roles) return ['Organization Content Admin'];
      if (school && 'Billing' in school.roles) return ['Billing'];
    } else {
      if (intersection(user.value.roles, ['Superuser', 'Support'])) {
        return ['Superuser', 'Content Manager', 'Support'];
      }
      if ('Content Manager' in user.value.roles) {
        return ['Content Manager'];
      }
    }

    return null;
  };

  const set_session = (session) => {
    sessionStore.set_session(session);
  };

    async function get_checkout_session(cart){
        // `mayHaveCompleted: true` - a dropped response doesn't mean the
        // checkout session wasn't created with Stripe; restapi.js's
        // error_intercept uses the softer "this may have been saved" wording.
        const response= await api.post('/user/checkout_session', {cart: cart}, { mayHaveCompleted: true });
        if(response.status === 200) {
            return response.data;
        }
    }

    async function get_session_status(session_id){
        const response= await api.get(`/user/session_status/${session_id}`);
        if(response.status === 200) {
            return response.data;
        }
    }

    async function CheckUser(email, course_code, demo, redirect_to) {
        try {
            const response= await api_no_intercept.post('/login_check', {email: email, demo: demo, course_code: course_code, redirect_to: redirect_to});
            if (response.status === 200) {
                return response.data;
            }
        }
        catch (error) {
            if(error.response && error.response.status >= 400 && error.response.status < 500 ) return {message: error.response.data.message};
            else if (error.response && error.response.status === 503 && error.response.data && error.response.data.maintenance_mode) {
                // Handle maintenance mode
                sessionStorage.setItem('maintenance_message', error.response.data.message || 'System is currently under maintenance. Please try again later.');
                window.location.pathname = '/maintenance';
                return {message: error.response.data.message || 'System is currently under maintenance. Please try again later.'};
            }
            // Re-throw unexpected errors
            throw error;
        }
    }

    async function VerifyUser(email, verification_code, redirect_to) {
        try {
            const response = await api_no_intercept.post('/verify_email', {email: email, verification_code: verification_code, redirect_to: redirect_to});
            if (response.status === 200) {
                set_session(response.data.session);
                return true;
            }
        }
        catch (error) {
            if(error.response && error.response.status >= 400 && error.response.status < 500 ) return error.response.data.message;
            // Re-throw unexpected errors
            throw error;
        }
    }

    async function GetOrganizations(page, rowsPerPage, sortBy, descending, filter, show_orgs_only) {
        const response = await api.get('/get_organizations', {params:
                {query: filter, page: page, rowsPerPage: rowsPerPage, sortBy: sortBy, descending: descending, show_orgs_only: show_orgs_only}});
        if (response.status === 200) {
            return response.data;
        }
    }

    async function GetUsers(organization_uid, campus_uid, program_uid, course_uid, page, rowsPerPage, sortBy, descending, filter, students, cohort_uid) {
        if (sessionStore.transitioning) {
            return {users: [], total: 0};
        }
        const response = await api.get('/users', {params:
                {organization_uid: organization_uid, campus_uid: campus_uid, program_uid: program_uid,
                    course_uid: course_uid, query: filter, page: page, rowsPerPage: rowsPerPage, sortBy: sortBy,
                    descending: descending, students: students, cohort_uid: cohort_uid}});
        if (response.status === 200) {
            return response.data;
        }
    }

    // Set one of the current user's display preferences. The response carries
    // the full effective set, so the session copy is replaced from it rather
    // than patched locally — the backend stays the source of truth for defaults.
    // Set a display preference. Omit user_uid for your own; pass one to set it
    // on another user from the user-management screen (Profile.vue serves both).
    // Only refresh the session copy when the target IS the logged-in user --
    // otherwise an admin editing someone else would adopt their preferences.
    async function SetPreference(key, value, user_uid){
        const response = await api.put(`/user/preferences/${key}`, {value: value, user_uid: user_uid || null});
        if (response.status === 200 && sessionStore.user
            && (!user_uid || user_uid === sessionStore.user.uid)) {
            sessionStore.user = {...sessionStore.user, preferences: response.data.preferences};
        }
        return response.data.preferences;
    }

    async function UpdateUser(u, new_demo){
        const response = await api.post('/update_user', {user: u, new_demo: new_demo ? new_demo: null});
        if (response.status === 200) {
            if (!user.value || u.uid === user.value.uid) {
                sessionStore.user = response.data.user;
                if(response.data.organization){
                    sessionStore.organization = response.data.organization;
                }
            }
            Notify.create({
              color: 'positive',
              message: `User ${response.data.user.username} updated successfully`,
              icon: 'save'
            })

            return response.data.user;
        }
    }

    async function ContactUs(new_demo){
        try {
            const response = await api.post('/contact_us', {new_demo: new_demo ? new_demo: null});
            if (response.status === 200) {
            }
        }
        catch (error) {
            if(error.response && error.response.status >= 400 && error.response.status < 500 ) return error.response.data.message;
        }
    }

    async function DeleteUser(user){
        const response = await api.delete(`/user/${user.uid}`);
    }

    async function Login(email, password, fingerprint) {
        try {
            const response = await api_no_intercept.post('/login', {email: email, password: password, fingerprint: fingerprint});
            if (response.status === 200) {
                set_session(response.data.session);
                return {success: true, redirect: response.data.redirect};
            }
        }
        catch (error) {
            if(error.response && error.response.status >= 400 && error.response.status < 500 ){
                console.warn(`Login Failed: ${error.response.data.message}`)
                localStorage.removeItem('logged_in');
                return {success: false, message: error.response.data.message};
            } 
            else if (error.response && error.response.status === 503) {
                // Handle maintenance mode
                console.log("Login: 503 maintenance mode");
                if (error.response.data && error.response.data.maintenance_mode) {
                    // Store maintenance message for the maintenance page
                    sessionStorage.setItem('maintenance_message', error.response.data.message || 'System is currently under maintenance. Please try again later.');
                    // Redirect to maintenance page - use pathname to avoid full reload
                    window.location.pathname = '/maintenance';
                }
                return {success: false, message: error.response.data.message || 'System is currently under maintenance. Please try again later.'};
            }
            else throw error;
        }
    }

    async function BecomeUser(uid) {
        try {
            console.log(`BecomeUser: Attempting to become user ${uid}`);
            sessionStore.transitioning = true;
            const response = await api_no_intercept.post(`/become_user/${uid}`);
            if (response && response.status === 200) {
                console.log(`BecomeUser: Successfully became user ${uid}`, response.data.session.user);
                set_session(response.data.session);
                
                await nextTick();
                console.log(`BecomeUser: After nextTick, default_route is:`, default_route.value);
                
                sessionStore.transitioning = false;
                return {success: true, redirect: default_route.value};
            }
        }
        catch (error) {
            sessionStore.transitioning = false;
            console.error(`BecomeUser: Failed to become user ${uid}`, error);
            if(error.response && error.response.status >= 400 && error.response.status < 500 ) {
                return error.response.data.message;
            }
            throw error;
        }
    }

    /**
     * Resolve the presigned-upload payload ({url, download_url}), or REJECT.
     *
     * This used to resolve `error.response.data.message` (a String) on a 4xx
     * and `undefined` on everything else - 5xx, network failure, an aborted
     * request - so every failure mode arrived at the caller as one shapeless
     * "no url". ProfilePicUploader turned that into
     * `new Error('Could not get an upload URL')`: an error with no status, no
     * server message and no axios cause, which is why GitLab #896 could not be
     * diagnosed from the alert at all. Worse, that synthetic message walks past
     * shouldDiscardRumErrorEvent's generic-axios-message fallback, so a 401
     * that error_intercept had ALREADY reported in full ("API error 401: POST
     * /get_upload_url", with status and the server's "session expired") got
     * filed a second time as a brand-new untagged Error Tracking issue.
     *
     * toStoreError is the same mapping region.js uses (see
     * services/apiClientError.js): a 4xx other than 401 becomes an
     * ApiClientError carrying the server's message and status, while a 401, a
     * 5xx, a network failure and a cancellation pass through untouched - so
     * handle401's HANDLED stamp survives and callers can gate on it.
     */
    async function GetUploadUrl(file_name, file_type) {
        try {
            const response = await api.post('/get_upload_url', {file_name: file_name, file_type: file_type});
            if (response.status === 200) {
                return response.data;
            }
            // A 2xx that isn't 200 has no payload contract. Rejecting keeps the
            // resolve-or-reject promise: it used to fall off the end here and
            // resolve undefined.
            throw new Error(`Unexpected status ${response.status} from /get_upload_url`);
        }
        catch (error) {
            throw toStoreError(error);
        }
    }

    async function Logout() {
        try {
            sessionStore.user = null;
            const response = await api_no_intercept.post('/logout');
            if (response.status === 200) {
                if(response.data.session) {
                    // Become user logout - restore previous session
                    set_session(response.data.session);
                }
                else {
                    // Normal logout - clear everything
                    sessionStore.clear_session();
                    localStorage.removeItem('logged_in');
                    sessionStore.loggedIn = false;
                }
            }
        }
        catch (error) {
            if(error.response && error.response.status >= 400 && error.response.status < 500 ) return error.response.data.message;
            // Ensure localStorage is cleared on error
            localStorage.removeItem('logged_in');
            sessionStore.loggedIn = false;
        }
    }

    async function CreateOTP(email, redirect_to) {
        try {
            const response = await api_no_intercept.post('/create_otp', {email: email, redirect_to: redirect_to});
            if (response.status === 200) {
                return true;
            }
        } catch (error) {
            if (error.response && error.response.status >= 400 && error.response.status < 500) return error.response.data.message;
            throw error;
        }
    }

    async function VerifyOTP(email, otp) {
        try {
            const response = await api_no_intercept.post('/verify_otp', {email: email, otp: otp});
            if (response.status === 200) {
                set_session(response.data.session);
                return response.data;
            }
        } catch (error) {
            if (error.response && error.response.status >= 400 && error.response.status < 500) return error.response.data.message;
            throw error;
        }
    }

    // Verify a credential without creating a session (for witness auth).
    // POST /login with verify_only answers {success, verify_only, user} (or
    // {success: false, message} for a bad credential) and neither creates a
    // session nor sets a cookie, so the result must never reach set_session:
    // the witness is only being checked, the signed-in user stays signed in.
    async function verifyOnlyLogin(credential) {
        try {
            const response = await api_no_intercept.post('/login', {...credential, verify_only: true});
            if (response.status === 200) {
                return response.data;
            }
        } catch (error) {
            if (error.response && error.response.status >= 400 && error.response.status < 500) {
                return {success: false, message: error.response.data.message};
            }
            throw error;
        }
    }

    // Verify email + password without creating a session (for witness auth)
    function VerifyCredentials(email, password) {
        return verifyOnlyLogin({email, password});
    }

    // Verify a badge/card fingerprint without creating a session (for witness
    // auth). The card counterpart of VerifyCredentials: Login(null, null, hash)
    // would sign the witness in over the top of the current user (#1161).
    function VerifyCard(fingerprint) {
        return verifyOnlyLogin({fingerprint});
    }

    // Verify OTP without creating a session (for witness auth)
    async function VerifyOTPOnly(email, otp) {
        try {
            const response = await api_no_intercept.post('/verify_otp', {email, otp, verify_only: true});
            if (response.status === 200) {
                return response.data;
            }
        } catch (error) {
            if (error.response && error.response.status >= 400 && error.response.status < 500) {
                return {success: false, message: error.response.data.message};
            }
        }
    }

    async function getUserSession(force = false) {
        try {
            const sess = await sessionStore.fetchSession(force);
            if(sess.user) return true;
        } catch (error) {
            if (error.response && error.response.status >= 400 && error.response.status < 500) return error.response.data.message;
            throw error;
        }
    }

    async function UpdateSession(org_id){
        const response = await api.post('/user_session', {organization_uid: org_id});
    }

    async function getUser(uid) {
        try {
            const response = await api.get(`/user/${uid}`);
            if (response.status === 200) {
                return response.data.user;
            }
            throw new Error(`Unexpected status ${response.status} from /user/${uid}`);
        } catch (error) {
            throw toStoreError(error);
        }
    }

    async function NewPassword(password, confirm_password, user_uid){
        try{
            const response = await api.post('/new_password', {password: password, user_uid: user_uid});
            if(response.status === 200) {
                Notify.create({
                  color: 'positive',
                  message: `User ${response.data.user.username} password updated successfully`,
                  icon: 'save'
                })
                return true;

            }
        }
        catch(error){
            if(error.response && error.response.status >= 400 && error.response.status < 500 ) return error.response.data.message;
            throw error;
        }
    }

    async function AddCourseCode(course_code){
        try{
            const response = await api.post('/add_course_code', {course_code: course_code});
            if(response.status === 200) {
                sessionStore.user = response.data.user;
                Notify.create({
                  color: 'positive',
                  message: `User ${sessionStore.user.username} successfully added to course`,
                  icon: 'save'
                })

                return response.data; // Return full response data including course_data
            }
        }
        catch(error){
            Notify.create({
              color: 'negative',
              message: `Course not found for code ${course_code}`,
              icon: 'error'
            })
            if(error.response && error.response.status >= 400 && error.response.status < 500 ) return error.response.data.message;
        }
    }

    async function ApplyPaymentCode(payment_code){
        try {
            // `silent: true` — restapi.js's error_intercept only honors it in the
            // response-less branch, so a real 5xx here still gets the normal
            // global toast; only the "we don't know if this reached the server"
            // case stays quiet, because StudentPayment.vue's apply_payment_code
            // shows its own code-specific inline message for exactly that shape
            // (issue #1109).
            const response = await api.post('/apply_payment_code', {payment_code: payment_code}, { silent: true });
            sessionStore.user = response.data.user;
            return true;
        } catch (error) {
            if (error.response && error.response.status >= 400 && error.response.status < 500) return error.response.data.message;
            throw error;
        }
    }

    async function GetUserData(url){
        const response = await api.get('/handle_users_data', {params: {url: url}});
        if(response.status === 200) {
            return response.data;
        }
    }

    async function AddEmail(user, email){
      const response = await api.post('/add_email', {uid: user.uid, email: email});
      if(response.status === 200) {
          return response.data;
      }
    }

    async function AddCard(user, fingerprint, name){
        const response = await api.post('/add_card', {uid: user.uid, fingerprint: fingerprint, name: name});
        if(response.status === 200) {
            return response.data;
        }
    }

    async function DeleteEmail(user, email, system){
        const response = await api.post('/delete_email', {uid: user.uid, system: system || 'email', system_key: email});
        if(response.status === 200) {
            return response.data;
        }
    }

    function base64urlToArrayBuffer(base64url) {
      // Convert Base64URL to standard Base64
      const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');

      // Decode Base64 string to raw binary string
      const binaryString = atob(base64);

      // Convert binary string to ArrayBuffer
      const len = binaryString.length;
      const buffer = new ArrayBuffer(len);
      const byteArray = new Uint8Array(buffer);

      for (let i = 0; i < len; i++) {
        byteArray[i] = binaryString.charCodeAt(i);
      }

      return buffer;
    }
    // Register a new passkey
    async function registerPasskey() {
        const results = await api.post('/auth/register-options');
        const options = JSON.parse(results.data.options);
        options['challenge'] = base64urlToArrayBuffer(options['challenge']);
        options['user']['id'] = base64urlToArrayBuffer(options['user']['id']);
        if (options['excludeCredentials']) {
            for (let i = 0; i < options['excludeCredentials'].length; i++) {
                options['excludeCredentials'][i]['id'] = base64urlToArrayBuffer(options['excludeCredentials'][i]['id']);
            }
        }
        console.log(options)
        try {
          const credential = await navigator.credentials.create({publicKey: options});
          const response = await api.post('/auth/register', {
              credential: credential,
          });

          return response.data
        }
        catch (error) {
          if(error.name === 'InvalidStateError'){
            Notify.create({
              color: 'info',
              message: 'This passkey is already registered with your account.',
            });
            // Recoverable, non-fatal outcome: resolve with an explicit
            // failure sentinel (mirroring loginWithPasskey) instead of
            // implicitly returning undefined, which the caller would
            // otherwise mistake for a successful {user: ...} payload.
            return {success: false};
          }
          else if(error.name === 'NotAllowedError'){
            // Thrown when the tab loses focus (e.g. the user alt-tabs) during
            // the network round-trip before navigator.credentials.create()
            // runs. This is a normal, user-recoverable outcome, not a bug.
            Notify.create({
              color: 'warning',
              textColor: 'dark',
              message: 'Passkey creation was interrupted because this tab lost focus. Please try again and keep this tab focused.',
            });
            return {success: false};
          }
          else{
            throw error;
          }
        }
    }

    // Authenticate with a passkey
    async function loginWithPasskey(conditional) {
      passkey_abort_controller.value = new AbortController();
      const signal = passkey_abort_controller.value.signal;

      // A WebAuthn assertion is signed over the specific challenge issued by
      // /auth/login-options, so recovering from a lost challenge means running
      // a brand-new ceremony (fresh options -> fresh navigator.credentials.get
      // -> fresh /auth/login), which necessarily re-prompts the authenticator.
      // The backend returns 400 {code: "challenge_missing"} when the session
      // holding the challenge was lost between the two calls (GitLab #856).
      // For a user-initiated login we transparently restart the ceremony
      // exactly ONCE — the second authenticator prompt is expected and
      // unavoidable. Conditional/autofill logins never retry: they stay silent
      // (consistent with a null assertion), so a background flow can't trigger
      // a surprise second prompt. maxAttempts hard-caps the loop so a backend
      // that kept returning challenge_missing could never spin it forever.
      const maxAttempts = conditional ? 1 : 2;

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        $q.loading.show();
        // Pass the signal to every request in this flow, not just
        // navigator.credentials.get() — otherwise aborting on unmount leaves
        // an in-flight /auth/login-options (or /auth/login) call to resolve
        // on its own regardless of whether the caller is still around.
        const results = await api.post('/auth/login-options', undefined, { signal })
        const options = JSON.parse(results.data.options);
        options['challenge'] = base64urlToArrayBuffer(options['challenge']);
        $q.loading.hide();
        const assertion = await navigator.credentials.get({
          publicKey: options,
          mediation: conditional ? 'conditional' : undefined,
          signal
        });
        // Conditional/autofill mediation can resolve with no credential (null)
        // when the browser has no selectable passkey and the visitor never
        // picked one — this isn't a rejection, so !124's NotFoundError handling
        // never sees it. Treat it as a benign no-op: don't POST "null" to
        // /auth/login (the backend 400s, which would surface a spurious error
        // toast to a visitor who never interacted with anything).
        if (!assertion) {
          $q.loading.hide();
          return {success: false};
        }
        $q.loading.show();

        try {
          const response = await api.post('/auth/login', {
            credentials: JSON.stringify(assertion),
          }, { signal });
          $q.loading.hide();
          set_session(response.data.session);
          return {success: true, redirect: response.data.redirect};
        } catch (error) {
          $q.loading.hide();
          const challengeMissing = error?.response?.data?.code === 'challenge_missing';
          if (challengeMissing) {
            // Conditional/autofill: never re-prompt and never surface an error —
            // abort silently like a null assertion, so a background flow stays
            // invisible to a visitor who never interacted with anything.
            if (conditional) {
              return {success: false};
            }
            // User-initiated: transparently restart the ceremony once. If the
            // freshly-issued challenge is ALSO lost, fall through and let the
            // caller show the normal login error.
            if (attempt < maxAttempts) {
              continue;
            }
          }
          // Every other failure (verification failure, network error, abort)
          // propagates to the caller unchanged.
          throw error;
        }
      }
    }

    async function passkey_register_complete() {
        const response = await api.post('/auth/register-complete');
        return response.json();
    }

    async function passkey_login_complete() {
        const response = await api.post('/auth/login-complete');
        return response.json();
    }

  return {
    user,
    device,
    deviceLoaded,
    setDevice,
    ensureDevice,
    passkey_abort_controller,
    loggedIn,
    lti_launch,
    org_for_user,
    default_route,
    user_role,
    is_staff,
    manage_content,
    passwordCompromised,
    showPasswordCompromiseBanner,
    check_roles,
    preference,
    show_integrations_tab,
    SetPreference,
    has_role_in_any_course,
    check_license,
    programs_for_organization,
    get_user_program,
    get_user_campus,
    available_roles,
    set_session,
    get_checkout_session,
    get_session_status,
    CheckUser,
    VerifyUser,
    GetOrganizations,
    GetUsers,
    UpdateUser,
    ContactUs,
    DeleteUser,
    Login,
    BecomeUser,
    GetUploadUrl,
    Logout,
    CreateOTP,
    VerifyOTP,
    VerifyCredentials,
    VerifyCard,
    VerifyOTPOnly,
    getUserSession,
    UpdateSession,
    getUser,
    NewPassword,
    AddCourseCode,
    ApplyPaymentCode,
    GetUserData,
    AddEmail,
    AddCard,
    DeleteEmail,
    registerPasskey,
    loginWithPasskey,
    campuses_for_organization,
    passkey_register_complete,
    passkey_login_complete,
    sessionStore
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
